import { useEffect, useState, useRef, useCallback } from 'react';

const API_BASE = '/api';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function InvoiceModal({ conversationId, open, onClose, onSent }) {
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailResult, setEmailResult] = useState(null); // { ok, error }
  const debounceRef = useRef(null);

  // Load invoice draft when modal opens
  useEffect(() => {
    if (!open || !conversationId) return;
    setDraft(null);
    setForm(null);
    setLoadError(null);
    setEmailResult(null);

    fetch(`${API_BASE}/invoices/${conversationId}`)
      .then(r => r.json())
      .then(data => {
        setDraft(data);
        const conv = data.conversation || {};
        const settings = data.settings || {};
        const matched = data.matchedBooking;

        setForm({
          recipient_name: conv.guest_name || '',
          recipient_address: conv.guest_address || '',
          recipient_email: conv.guest_email || '',
          amount: conv.invoice_amount ?? matched?.gross_payout ?? '',
          currency: matched?.currency || settings.currency || 'EUR',
          lineItemDescription: settings.lineItemDescription || 'Accommodation',
          invoiceDate: todayISO(),
        });
      })
      .catch(e => setLoadError(e.message));
  }, [open, conversationId]);

  // Debounced PATCH on field changes
  const patchFields = useCallback((fields) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const patchBody = {};
      if (fields.recipient_email !== undefined) patchBody.guest_email = fields.recipient_email;
      if (fields.recipient_address !== undefined) patchBody.guest_address = fields.recipient_address;
      if (fields.amount !== undefined && fields.amount !== '') {
        const n = parseFloat(fields.amount);
        if (!isNaN(n)) patchBody.invoice_amount = n;
      }
      if (Object.keys(patchBody).length === 0) return;

      fetch(`${API_BASE}/invoices/${conversationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patchBody),
      }).catch(e => console.error('PATCH invoice failed:', e));
    }, 600);
  }, [conversationId]);

  const handleChange = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    patchFields({ [field]: value });
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await fetch(`${API_BASE}/invoices/${conversationId}/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'PDF error' }));
        throw new Error(err.error || 'PDF failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${draft?.invoiceNumber || conversationId}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
    } catch (e) {
      alert(`Download failed: ${e.message}`);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleEmailGuest = async () => {
    setEmailLoading(true);
    setEmailResult(null);
    try {
      const res = await fetch(`${API_BASE}/invoices/${conversationId}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Email failed');
      setEmailResult({ ok: true });
      onSent?.();
    } catch (e) {
      setEmailResult({ error: e.message });
    } finally {
      setEmailLoading(false);
    }
  };

  if (!open) return null;

  const emailValid = EMAIL_RE.test(form?.recipient_email || '');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Invoice {draft?.invoiceNumber ? `— ${draft.invoiceNumber}` : ''}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4 flex-1">
          {loadError && (
            <p className="text-red-500 text-sm mb-4">Failed to load: {loadError}</p>
          )}
          {!form && !loadError && (
            <p className="text-gray-400 text-sm">Loading…</p>
          )}
          {form && (
            <div className="space-y-4">
              {/* Recipient */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Recipient Name</label>
                <input
                  type="text"
                  value={form.recipient_name}
                  onChange={e => handleChange('recipient_name', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Recipient Address</label>
                <textarea
                  rows={3}
                  value={form.recipient_address}
                  onChange={e => handleChange('recipient_address', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  placeholder="Street, City, Country"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Recipient Email</label>
                <input
                  type="email"
                  value={form.recipient_email}
                  onChange={e => handleChange('recipient_email', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="guest@example.com"
                />
              </div>

              {/* Amount + Currency */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount}
                    onChange={e => handleChange('amount', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="0.00"
                  />
                </div>
                <div className="w-28">
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Currency</label>
                  <select
                    value={form.currency}
                    onChange={e => handleChange('currency', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {['EUR', 'USD', 'GBP', 'CHF', 'AED'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Line item description */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Line Item Description</label>
                <input
                  type="text"
                  value={form.lineItemDescription}
                  onChange={e => handleChange('lineItemDescription', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Invoice date */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Invoice Date</label>
                <input
                  type="date"
                  value={form.invoiceDate}
                  onChange={e => handleChange('invoiceDate', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Email result */}
              {emailResult?.ok && (
                <p className="text-green-600 text-sm font-medium">Email sent successfully!</p>
              )}
              {emailResult?.error && (
                <p className="text-red-500 text-sm">Error: {emailResult.error}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer buttons */}
        {form && (
          <div className="px-5 py-4 border-t border-gray-200 flex gap-3">
            <button
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              className="flex-1 px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition"
            >
              {pdfLoading ? 'Generating…' : '⬇️ Download PDF'}
            </button>
            <button
              onClick={handleEmailGuest}
              disabled={emailLoading || !emailValid}
              className="flex-1 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
              title={!emailValid ? 'Enter a valid recipient email first' : ''}
            >
              {emailLoading ? 'Sending…' : '✉️ Email to Guest'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
